export interface AdClickIds {
  gclid?: string
  rdt_cid?: string
  epik?: string
}

export interface AdTouch {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  landing_url: string
  timestamp: string
}

export interface AdAttribution {
  firstTouch?: AdTouch
  latestTouch?: AdTouch
  clickIds?: AdClickIds
}

type LocationLike = Pick<Location, 'href' | 'search'>

const STORAGE_KEY = 'pb_ad_attribution_v1'
const MAX_PARAM_LENGTH = 2048
const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'rdt_cid',
  'epik',
] as const

const sanitizeValue = (value: string | null): string | undefined => {
  if (!value) return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  return trimmed.slice(0, MAX_PARAM_LENGTH)
}

const toLandingUrl = (href: string): string => {
  try {
    const url = new URL(href)
    url.hash = ''
    return url.toString()
  } catch {
    return href
  }
}

const readStoredAttribution = (): AdAttribution | null => {
  if (typeof window === 'undefined') return null

  const raw = window.localStorage.getItem(STORAGE_KEY)
  if (!raw) return null

  try {
    return JSON.parse(raw) as AdAttribution
  } catch {
    window.localStorage.removeItem(STORAGE_KEY)
    return null
  }
}

const writeStoredAttribution = (value: AdAttribution): void => {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
}

const getTouchFromSearchParams = (
  params: URLSearchParams,
  landingUrl: string,
  timestamp: string
): AdTouch | null => {
  const touch: AdTouch = {
    landing_url: landingUrl,
    timestamp,
  }

  let hasAttribution = false

  for (const key of ATTRIBUTION_KEYS) {
    if (!key.startsWith('utm_')) continue
    const value = sanitizeValue(params.get(key))
    if (!value) continue
    touch[key] = value
    hasAttribution = true
  }

  return hasAttribution ? touch : null
}

const getClickIdsFromSearchParams = (params: URLSearchParams): AdClickIds => {
  const clickIds: AdClickIds = {}

  for (const key of ATTRIBUTION_KEYS) {
    if (key.startsWith('utm_')) continue
    const value = sanitizeValue(params.get(key))
    if (value) {
      clickIds[key] = value
    }
  }

  return clickIds
}

const hasClickIds = (clickIds: AdClickIds): boolean =>
  Boolean(clickIds.gclid || clickIds.rdt_cid || clickIds.epik)

const buildTouchFromLanding = (
  landingUrl: string,
  timestamp: string
): AdTouch => ({
  landing_url: landingUrl,
  timestamp,
})

export const captureFromLocation = (
  locationLike?: LocationLike
): AdAttribution | null => {
  if (typeof window === 'undefined') return null

  const location = locationLike ?? window.location
  const params = new URLSearchParams(location.search)
  const landingUrl = toLandingUrl(location.href)
  const timestamp = new Date().toISOString()
  const nextTouch = getTouchFromSearchParams(params, landingUrl, timestamp)
  const nextClickIds = getClickIdsFromSearchParams(params)
  const nextLandingTouch =
    nextTouch ??
    (hasClickIds(nextClickIds)
      ? buildTouchFromLanding(landingUrl, timestamp)
      : null)
  const hasNewAttribution =
    Boolean(nextLandingTouch) || hasClickIds(nextClickIds)

  if (!hasNewAttribution) {
    return readStoredAttribution()
  }

  const existing = readStoredAttribution()
  const mergedClickIds: AdClickIds = {
    ...(existing?.clickIds ?? {}),
    ...nextClickIds,
  }

  const nextAttribution: AdAttribution = {
    firstTouch:
      existing?.firstTouch ??
      nextLandingTouch ??
      existing?.latestTouch ??
      undefined,
    latestTouch: nextLandingTouch ?? existing?.latestTouch ?? undefined,
    clickIds: hasClickIds(mergedClickIds) ? mergedClickIds : undefined,
  }

  writeStoredAttribution(nextAttribution)
  return nextAttribution
}

export const getAttribution = (): AdAttribution | null =>
  readStoredAttribution()

export const serializeForCheckout = (): AdAttribution | null =>
  readStoredAttribution()

export const clearAttribution = (): void => {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(STORAGE_KEY)
}
